import { z } from 'zod';
import {
  workbookDefinitionSchema,
  workbookOutputSchema,
  workbookSheetSchemaOf,
  workbookSourceSchema,
  workbookTaskRuleSchema,
  type WorkbookDefinition,
} from '@erp/shared-types';
import { applyOutlineOp } from '@erp/workbooks';

/**
 * The lists of a Definition an agent edit addresses by id. Files are uploaded
 * by the user, never written by an edit; removing one is allowed.
 */
export const WORKBOOK_AGENT_COLLECTIONS = [
  'sources',
  'sheets',
  'outputs',
  'taskRules',
  'processSteps',
  'files',
] as const;
type Collection = (typeof WORKBOOK_AGENT_COLLECTIONS)[number];
const collectionSchema = z.enum(WORKBOOK_AGENT_COLLECTIONS);

const wholeItemSchema = z
  .record(z.string(), z.unknown())
  .describe(
    'The whole item, as get_workbook shows it and the definition schema defines it (formulas as Excel text).',
  );

/**
 * One operation of an agent edit. Several apply together as one edit: one
 * step of the edit history, undone at once.
 */
export const workbookAgentOperationSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('upsert'),
    collection: collectionSchema.exclude(['files']),
    item: wholeItemSchema,
    index: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(
        'Where a new item goes in its list (0 is first); appended when omitted. An existing item keeps its place.',
      ),
  }),
  z.object({
    op: z.literal('update'),
    collection: collectionSchema.exclude(['files']),
    id: z.string().min(1),
    changes: z
      .record(z.string(), z.unknown())
      .describe(
        'Top-level fields to replace on the item, e.g. {"filters":[...]} or {"validations":[...]}; other fields are kept.',
      ),
  }),
  z.object({
    op: z.literal('remove'),
    collection: collectionSchema,
    id: z.string().min(1),
  }),
  z.object({
    op: z.literal('set_workbook'),
    name: z.string().optional(),
    instructions: z.string().optional(),
    sopAutoApply: z.boolean().optional(),
  }),
]);
export type WorkbookAgentOperation = z.infer<
  typeof workbookAgentOperationSchema
>;

type Item = { id: string };
/**
 * The schema an item of `collection` must pass. A Worksheet's is chosen by its
 * kind, so a refusal names the field instead of "Invalid input". Steps are
 * parsed with the whole Definition: their Parents and links need neighbours.
 */
function itemSchema(
  collection: Exclude<Collection, 'files'>,
  value: unknown,
): z.ZodType {
  switch (collection) {
    case 'sources':
      return workbookSourceSchema;
    case 'sheets':
      return workbookSheetSchemaOf(value);
    case 'outputs':
      return workbookOutputSchema;
    case 'taskRules':
      return workbookTaskRuleSchema;
    case 'processSteps':
      return z.object({ id: z.string().min(1) }).loose();
  }
}

/** Why an edit was refused, written for the model to act on. */
export class WorkbookAgentEditError extends Error {}

function issuesText(error: z.ZodError): string {
  return error.issues
    .map((issue) =>
      issue.path.length
        ? `${issue.path.join('.')}: ${issue.message}`
        : issue.message,
    )
    .join('; ');
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Refusals for the Report components in a written output that nest their
 * fields under `config`. A Definition read back repairs those (for reports
 * saved before this was refused), so this reads what the agent wrote, before
 * any parse: a repaired write would drop a bad nested field without a word.
 */
export function nestedReportConfigErrors(output: unknown): string[] {
  if (!isObject(output) || output.kind !== 'report') return [];
  const layout = isObject(output.layout) ? output.layout : {};
  const components = Array.isArray(layout.items)
    ? layout.items.map((item) => (isObject(item) ? item.component : null))
    : Array.isArray(layout.data)
      ? layout.data.flatMap((row) => (Array.isArray(row) ? row : []))
      : [];
  return components.flatMap((component) =>
    isObject(component) && 'config' in component
      ? [
          `Report component ${String(component.id)}: its fields are nested under "config"; set them directly on the component beside type and id, as report_component_catalog shows it.`,
        ]
      : [],
  );
}

/** Throws the refusals for any written output that nests a Report component's fields. */
export function refuseNestedReportConfig(
  outputs: unknown,
  label = 'The edited workbook',
) {
  const errors = (Array.isArray(outputs) ? outputs : []).flatMap(
    nestedReportConfigErrors,
  );
  if (errors.length)
    throw new WorkbookAgentEditError(`${label}: ${errors.join(' ')}`);
}

function parsedItem(
  collection: Exclude<Collection, 'files'>,
  value: unknown,
  label: string,
): Item {
  if (collection === 'outputs') refuseNestedReportConfig([value], label);
  const parsed = itemSchema(collection, value).safeParse(value);
  if (!parsed.success)
    throw new WorkbookAgentEditError(`${label}: ${issuesText(parsed.error)}`);
  const item = parsed.data as Item;
  if (typeof item.id !== 'string')
    throw new WorkbookAgentEditError(`${label}: the item needs an id.`);
  // Steps keep their fields as written so the Definition fills omitted Parents.
  return collection === 'processSteps' ? (value as Item) : item;
}

function list(definition: WorkbookDefinition, collection: Collection): Item[] {
  return definition[collection] as Item[];
}

function withList(
  definition: WorkbookDefinition,
  collection: Collection,
  items: readonly Item[],
): WorkbookDefinition {
  return { ...definition, [collection]: items };
}

function upsert(
  definition: WorkbookDefinition,
  collection: Exclude<Collection, 'files'>,
  item: Item,
  index: number | undefined,
): WorkbookDefinition {
  const items = list(definition, collection);
  if (items.some((entry) => entry.id === item.id))
    return withList(
      definition,
      collection,
      items.map((entry) => (entry.id === item.id ? item : entry)),
    );
  const at = Math.min(index ?? items.length, items.length);
  return withList(definition, collection, [
    ...items.slice(0, at),
    item,
    ...items.slice(at),
  ]);
}

function existing(
  definition: WorkbookDefinition,
  collection: Collection,
  id: string,
  label: string,
): Item {
  const found = list(definition, collection).find((entry) => entry.id === id);
  if (!found)
    throw new WorkbookAgentEditError(`${label}: no ${collection} item ${id}.`);
  return found;
}

/**
 * Removes an item and what only pointed at it: a Worksheet's tabs in File
 * Outputs and its Task rules; any item's links from Instructions, whose
 * Instructions under its Execution root become unassigned; a step's children
 * move up to its Parent. Worksheets reading a removed item are kept: their
 * Diagnostics say what to repoint.
 */
export function removeWorkbookItem(
  definition: WorkbookDefinition,
  collection: Collection,
  id: string,
): WorkbookDefinition {
  if (collection === 'processSteps')
    return applyOutlineOp(definition, { type: 'remove', id });
  if (collection === 'taskRules')
    return {
      ...definition,
      taskRules: definition.taskRules.filter((rule) => rule.id !== id),
    };
  const detached = withList(
    applyOutlineOp(definition, { type: 'detachItem', itemId: id }),
    collection,
    list(definition, collection).filter((entry) => entry.id !== id),
  );
  if (collection !== 'sheets') return detached;
  return {
    ...detached,
    outputs: detached.outputs.map((output) => {
      if (output.kind === 'sink')
        return output.sheetId === id
          ? { ...output, sheetId: undefined }
          : output;
      return output.kind === 'file'
        ? { ...output, tabs: output.tabs.filter((tab) => tab.sheetId !== id) }
        : output;
    }),
    taskRules: detached.taskRules.filter((rule) => rule.sheetId !== id),
  };
}

function applyOperation(
  definition: WorkbookDefinition,
  operation: WorkbookAgentOperation,
  label: string,
): WorkbookDefinition {
  switch (operation.op) {
    case 'upsert':
      return upsert(
        definition,
        operation.collection,
        parsedItem(operation.collection, operation.item, label),
        operation.index,
      );
    case 'update': {
      const current = existing(
        definition,
        operation.collection,
        operation.id,
        label,
      );
      return upsert(
        definition,
        operation.collection,
        parsedItem(
          operation.collection,
          { ...current, ...operation.changes, id: current.id },
          label,
        ),
        undefined,
      );
    }
    case 'remove':
      existing(definition, operation.collection, operation.id, label);
      return removeWorkbookItem(definition, operation.collection, operation.id);
    case 'set_workbook': {
      const { op: _op, ...fields } = operation;
      return { ...definition, ...fields };
    }
  }
}

/**
 * Applies an agent's operations in order to `definition` (formulas as written:
 * Excel text), then validates the whole result as a Definition. All or
 * nothing: a refused operation or an invalid result throws a
 * {@link WorkbookAgentEditError} naming it, and nothing applies.
 */
export function applyWorkbookAgentOperations(
  definition: WorkbookDefinition,
  operations: readonly WorkbookAgentOperation[],
): WorkbookDefinition {
  const edited = operations.reduce(
    (current, operation, index) =>
      applyOperation(
        current,
        operation,
        `Operation ${index + 1} (${operation.op}${'collection' in operation ? ` ${operation.collection}` : ''})`,
      ),
    definition,
  );
  refuseNestedReportConfig(edited.outputs);
  const parsed = workbookDefinitionSchema.safeParse(edited);
  if (!parsed.success)
    throw new WorkbookAgentEditError(
      `The edited workbook is invalid: ${issuesText(parsed.error)}`,
    );
  return parsed.data;
}
